import { useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { AdressAnsicht, AdressListe as AdressListeAntwort, ApiClient, Rechnungsangaben }
  from "@kundeninfo/api-client";
import { Modal, ModalFuss, darfAbbrechen, useBeforeUnload, useBestaetigung, useFormularZustand }
  from "@kundeninfo/ui";
import { Auswahl, Formular, Hinweis } from "@kundeninfo/ui/me-design";
import { KiKnopf } from "../components/meHuellen.js";
import { alsEintrag, alsText, geordnet } from "../adressText.js";
import { AdressPanel } from "./AdressPanel.js";
import type { AdressPanelClient } from "./AdressPanel.js";
import "./BeziehungRechnungsangaben.css";

/**
 * RECHNUNGSADRESSE UND RECHNUNGS-E-MAIL AN DER KARTE DER KUNDENBEZIEHUNG (Spec „Rechnungsadresse und
 * Rechnungs-E-Mail je Unternehmen", E342, E343, R10, R13).
 *
 * Die Adressen bleiben am Kunden gepflegt; die Beziehung bestimmt nur, welche gilt. Gewählt wird aus der
 * Auswahl des Servers (eigene und geerbte, jede Rolle) — oder im selben Ablauf neu angelegt: Das Adresspanel
 * des Adressen-Reiters öffnet sich seitlich daneben, vorgewählt „Rechnungsadresse" bzw. „Rechnungs-E-Mail",
 * und die neue Adresse steht danach im Feld. Festgelegt wird erst mit „Speichern" und nach der Bestätigung.
 *
 * Ohne Angabe gilt der Rückfall (E343), die Karte zeigt ihn als „Vorgabe". Eine VERWAISTE Angabe (archiviert
 * oder nicht mehr beim Kunden, R10) liefert der Server als `null` mit Kennzeichen: Die Karte warnt zuerst
 * (Schlechtestes oben), das Feld steht auf „Vorgabe verwenden", und Speichern schickt `null` — der Verweis ist
 * damit gelöst.
 *
 * Eine NEU ANGELEGTE Adresse (Rolle BILLING) wird sofort Rückfall für alle Beziehungen des Kunden: Die Karte
 * holt danach Rückfall und Auswahl neu (der Entwurf bleibt), und `onAdressenGeaendert` lässt den Einbauort
 * die Nachbarkarten über `adressStand` nachladen.
 *
 * Eigener CSS-Block `bra__`.
 */
export type RechnungsangabenClient = Pick<ApiClient, "getRelationBilling" | "setRelationBilling">
  & AdressPanelClient;

export type BeziehungRechnungsangabenProps = {
  client: RechnungsangabenClient;
  kundeId: string;
  kundeName: string;
  relationId: string;
  companyName: string;
  /** Über der Karte steht ein fremdes Panel (Stammdaten-Formular) — „ändern" ist dann gesperrt. */
  verdeckt?: boolean;
  /** Zähler „Adressen des Kunden geändert" vom Einbauort: Jede Änderung lädt Rückfall und Auswahl nach. */
  adressStand?: number;
  /** Nach einer Neuanlage an DIESER Karte — der Einbauort zählt `adressStand` hoch. Ohne Angabe lädt
   *  die Karte nur selbst nach. */
  onAdressenGeaendert?: () => void;
};

type Feld = "billingAddressId" | "billingEmailAddressId";
/** Der Optionswert „neu anlegen" — keine UUID, also nie eine gültige Wahl. */
const NEU = "__neu";

const FELDER: readonly { feld: Feld; name: string; neu: string; wahl: string; fehlt: string;
  angabe: "billingAddress" | "billingEmailAddress"; verwaist: "billingAddressVerwaist" | "billingEmailAddressVerwaist";
  auswahl: "postal" | "email" }[] = [
  { feld: "billingAddressId", name: "Rechnungsadresse", neu: "Neue Rechnungsadresse anlegen …",
    wahl: "POSTAL_BILLING", fehlt: "beim Kunden ist keine Postanschrift hinterlegt",
    angabe: "billingAddress", verwaist: "billingAddressVerwaist", auswahl: "postal" },
  { feld: "billingEmailAddressId", name: "Rechnungs-E-Mail", neu: "Neue Rechnungs-E-Mail anlegen …",
    wahl: "EMAIL_BILLING", fehlt: "beim Kunden ist keine E-Mail-Adresse hinterlegt",
    angabe: "billingEmailAddress", verwaist: "billingEmailAddressVerwaist", auswahl: "email" },
];

const vorgabeText = (a: AdressAnsicht): string => `Vorgabe — ${alsText(a)}`;

/** Die Warnungen verwaister Angaben (R10) — an Karte und Panel dieselben. */
function warnungen(s: Rechnungsangaben): ReactElement[] {
  return FELDER.filter((f) => s[f.verwaist]).map((f) => (
    <Hinweis key={f.feld} art="warnung">
      {`Die festgelegte ${f.name} ist archiviert oder gehört nicht mehr zum Kunden — ${
        s.rueckfall[f.angabe] === null ? "eine Vorgabe gibt es nicht." : "es gilt die Vorgabe."}`}
    </Hinweis>
  ));
}

type Entwurf = { version: number; billingAddressId: string | null; billingEmailAddressId: string | null };
/** Eine verwaiste Angabe kommt als `null` — der Entwurf steht dann auf „Vorgabe verwenden" (R10). */
const entwurfAus = (s: Rechnungsangaben): Entwurf => ({
  version: s.version, billingAddressId: s.billingAddress?.id ?? null,
  billingEmailAddressId: s.billingEmailAddress?.id ?? null,
});

export function BeziehungRechnungsangaben(p: BeziehungRechnungsangabenProps): ReactElement {
  const [stand, setStand] = useState<Rechnungsangaben | null>(null);
  /** Laden der Karte bzw. der Adressarten für die Neuanlage — zwei getrennte Befunde. */
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  const [artenFehler, setArtenFehler] = useState<string | null>(null);
  const [entwurf, setEntwurf] = useState<Entwurf | null>(null);
  const [anlegen, setAnlegen] = useState<{ feld: Feld; arten: AdressListeAntwort["arten"] } | null>(null);
  const [konflikt, setKonflikt] = useState<{ von: string; fremd: Rechnungsangaben | null } | null>(null);
  const eingabenBehalten = useRef(false);
  const [oeffnet, setOeffnet] = useState(false);
  /** Riegel gegen späte Antworten nach einem Wechsel der Beziehung (Muster KundeBeziehungen). */
  const lauf = useRef(0);
  // Der jeweils aktuelle Stand von `verdeckt` — für die Prüfung NACH dem Abruf in `oeffnen` (Closure wäre veraltet).
  const verdecktJetzt = useRef(p.verdeckt === true);
  verdecktJetzt.current = p.verdeckt === true;
  const { bestaetigen } = useBestaetigung();
  const { client, relationId, adressStand } = p;

  useEffect(() => {
    const meiner = ++lauf.current;
    setStand(null); setEntwurf(null); setAnlegen(null); setKonflikt(null); setLadeFehler(null); setArtenFehler(null);
    client.getRelationBilling(relationId)
      .then((s) => { if (meiner === lauf.current) setStand(s); })
      .catch((fehler: unknown) => {
        if (meiner === lauf.current) {
          setLadeFehler(fehler instanceof ApiError ? fehler.message
            : "Die Rechnungsangaben konnten nicht geladen werden.");
        }
      });
    return () => { lauf.current += 1; };
  }, [client, relationId]);

  /** Nach einer Neuanlage: Rückfall und Auswahl frisch, Angaben, Version und Entwurf bleiben. */
  async function nachladen(): Promise<void> {
    const meiner = lauf.current;
    try {
      const frisch = await client.getRelationBilling(relationId);
      if (meiner === lauf.current) {
        setStand((s) => (s === null ? s : { ...s, rueckfall: frisch.rueckfall, auswahl: frisch.auswahl }));
      }
    } catch { /* die Karte behält ihren Stand; das nächste Laden holt ihn */ }
  }
  const gesehen = useRef(adressStand);
  useEffect(() => {
    if (adressStand === gesehen.current) return;
    gesehen.current = adressStand;
    void nachladen();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- nur der Zähler löst aus
  }, [adressStand]);

  /** 409: fremder Stand mit „Neu laden" und „Meine Eingaben behalten" (Muster KundeKonditionen). */
  const formular = useFormularZustand<Rechnungsangaben>({
    speichern: async () => {
      if (entwurf === null) throw new Error("Kein Panel offen.");
      try {
        return await client.setRelationBilling(relationId, entwurf);
      } catch (fehler) {
        if (fehler instanceof ApiError && fehler.status === 409) {
          setKonflikt({ von: fehler.conflict?.changedBy ?? "",
                        fremd: (fehler.conflict?.current as Rechnungsangaben | undefined) ?? null });
        }
        throw fehler;
      }
    },
    neuLaden: async () => {
      const behalten = eingabenBehalten.current;
      eingabenBehalten.current = false;
      const frisch = konflikt?.fremd ?? await client.getRelationBilling(relationId);
      setStand(frisch);
      // Behalten rückt nur die Versionsbasis vor — sonst liefe das nächste Speichern wieder in die 409.
      setEntwurf((alt) => (behalten && alt !== null ? { ...alt, version: frisch.version } : entwurfAus(frisch)));
      setKonflikt(null);
    },
    bekannteFelder: ["billingAddressId", "billingEmailAddressId"],
  });
  const speichert = formular.laeuft;

  const geaendert = entwurf !== null && stand !== null
    && (entwurf.billingAddressId !== entwurfAus(stand).billingAddressId
        || entwurf.billingEmailAddressId !== entwurfAus(stand).billingEmailAddressId);
  useBeforeUnload(geaendert);

  /**
   * Öffnen holt den Stand frisch (Gesamtreview W2): Die Version ist die der ganzen Kundenbeziehung (R11), und
   * jede Änderung daran seit dem Einhängen (Debitorennummer, Konditionen) führte sonst zu einem falschen 409.
   * Scheitert das Laden, öffnet das Panel mit dem bekannten Stand — ein echter Konflikt meldet sich dann beim
   * Speichern.
   */
  async function oeffnen(): Promise<void> {
    const meiner = lauf.current;
    setOeffnet(true);
    const frisch = await client.getRelationBilling(relationId).catch(() => null);
    setOeffnet(false);
    if (meiner !== lauf.current) return;
    if (frisch !== null) setStand(frisch);
    // Steht inzwischen ein fremdes Panel darüber, öffnet dieses nicht mehr daneben.
    if (verdecktJetzt.current) return;
    const basis = frisch ?? stand;
    if (basis === null) return;
    setEntwurf(entwurfAus(basis)); setArtenFehler(null); setKonflikt(null); formular.zuruecksetzen();
  }

  async function abbrechen(): Promise<void> {
    if (speichert || anlegen !== null) return;
    if (!(await darfAbbrechen(geaendert))) return;
    setEntwurf(null);
  }

  async function neuAnlegen(feld: Feld): Promise<void> {
    const meiner = lauf.current;
    // Ein alter Ladefehler der Adressarten gilt für einen neuen Versuch nicht mehr.
    setArtenFehler(null);
    try {
      const antwort = await client.listAddresses("customers", p.kundeId);
      if (meiner === lauf.current) setAnlegen({ feld, arten: antwort.arten });
    } catch (fehler) {
      if (meiner === lauf.current) {
        setArtenFehler(fehler instanceof ApiError ? fehler.message : "Die Adressarten konnten nicht geladen werden.");
      }
    }
  }

  async function speichern(): Promise<void> {
    if (!(await bestaetigen({
      titel: `Rechnungsangaben für ${p.companyName} speichern?`,
      text: "Sie gelten für neue Belege und für alle noch nicht festgeschriebenen Belege ohne eigene Wahl.",
      bestaetigenText: "Speichern",
    }))) return;
    const meiner = lauf.current;
    const neu = await formular.aktion();
    if (neu === undefined || meiner !== lauf.current) return;
    setStand(neu);
    setEntwurf(null);
  }

  if (ladeFehler !== null) return <Hinweis art="fehler">{ladeFehler}</Hinweis>;
  if (stand === null) return <p className="bra__laden">Rechnungsangaben werden geladen …</p>;

  return (
    <div className="bra" role="group" aria-label={`Rechnungsangaben ${p.companyName}`}>
      {/* Schlechtestes oben: verwaiste Angaben vor den Werten. */}
      {warnungen(stand)}
      {FELDER.map((f) => {
        const angabe = stand[f.angabe];
        const vorgabe = stand.rueckfall[f.angabe];
        return (
          <p key={f.feld} className="bra__zeile">
            {f.name}: {angabe !== null ? alsText(angabe)
              : vorgabe !== null ? vorgabeText(vorgabe) : `nicht festgelegt — ${f.fehlt}`}
          </p>
        );
      })}
      <span className="bra__aktionen">
        <KiKnopf name={`Rechnungsangaben für ${p.companyName} ändern`}
          deaktiviert={entwurf !== null || oeffnet || p.verdeckt === true}
          onClick={() => { void oeffnen(); }}>
          Rechnungsangaben ändern
        </KiKnopf>
      </span>

      {entwurf !== null && (
        <Modal titel={`Rechnungsangaben — ${p.companyName}`} onAbbrechen={abbrechen}
          abbrechenGesperrt={speichert || anlegen !== null}>
          {/* Schlechtestes oben: Fehler vor den Warnungen. */}
          {formular.meldung !== null && <Hinweis art="fehler">{formular.meldung}</Hinweis>}
          {artenFehler !== null && <Hinweis art="fehler">{artenFehler}</Hinweis>}
          {warnungen(stand)}
          <Formular beschriftung="oben" {...formular.formularProps}
            hinweis={konflikt === null ? undefined : (
              <span>
                {konflikt.von !== "" ? `${konflikt.von} hat diese Rechnungsangaben inzwischen geändert.`
                  : "Diese Rechnungsangaben wurden inzwischen geändert."}
                {" "}Deine Eingaben sind nicht verloren — „Neu laden“ übernimmt den aktuellen Stand.{" "}
                <KiKnopf className="ki-eingaben-behalten"
                  onClick={() => { eingabenBehalten.current = true; void formular.neuLaden(); }}>
                  Meine Eingaben behalten
                </KiKnopf>
              </span>
            )}>
            {FELDER.map((f) => {
              const vorgabe = stand.rueckfall[f.angabe];
              const fehler = formular.feldFehler[f.feld];
              const liste = geordnet(stand.auswahl[f.auswahl]);
              // K1: Eine festgelegte Angabe, die nicht in der Auswahl steht (geerbt von einem nicht einsehbaren
              // Vorfahren), bleibt als eigene Option wählbar — sonst hätte das Feld keinen gültigen Wert
              // (Muster `eigeneWahl` in BelegParteien).
              const angabe = stand[f.angabe];
              const eigene = angabe !== null && !liste.some((a) => a.id === angabe.id) ? angabe : null;
              return (
                <Auswahl key={f.feld} id={`bra-${relationId}-${f.feld}`} name={`bra-${relationId}-${f.feld}`}
                  beschriftung={f.name} wert={entwurf[f.feld] ?? ""}
                  {...(vorgabe === null ? {} : { hilfe: vorgabeText(vorgabe) })}
                  {...(fehler === undefined ? {} : { fehler })}
                  onGeaendert={(d) => {
                    // „Neu anlegen" ist keine Wahl: Das Feld springt zurück, bis die neue Adresse da ist.
                    if (d.wert === NEU) { void neuAnlegen(f.feld); return; }
                    setEntwurf((alt) => (alt === null ? alt : { ...alt, [f.feld]: d.wert === "" ? null : d.wert }));
                  }}>
                  <option value="">Vorgabe verwenden</option>
                  {eigene !== null && <option value={eigene.id}>{alsEintrag(eigene)} — festgelegt</option>}
                  {liste.map((a) => (
                    <option key={a.id} value={a.id}>{alsEintrag(a)}</option>
                  ))}
                  <option value={NEU}>{f.neu}</option>
                </Auswahl>
              );
            })}
          </Formular>
          <ModalFuss speichernText="Speichern" speichernGesperrt={formular.zustand !== "bereit" || anlegen !== null}
            abbrechenGesperrt={speichert || anlegen !== null} onSpeichern={() => { void speichern(); }}
            onAbbrechen={abbrechen} />
        </Modal>
      )}

      {/* Seitlich daneben (Hausregel „Sub-Inhalte seitlich"): dasselbe Adresspanel wie im Adressen-Reiter. */}
      {anlegen !== null && (() => {
        const f = FELDER.find((x) => x.feld === anlegen.feld)!;
        return (
          <AdressPanel client={client} besitzer="customers" ownerId={p.kundeId} arten={anlegen.arten}
            vorlage={null} startWahl={f.wahl} namensVorschlag={p.kundeName}
            onAbbrechen={() => setAnlegen(null)}
            onGespeichert={(neu) => {
              setAnlegen(null);
              setStand((s) => (s === null ? s
                : { ...s, auswahl: { ...s.auswahl, [f.auswahl]: [...s.auswahl[f.auswahl], neu] } }));
              setEntwurf((alt) => (alt === null ? alt : { ...alt, [f.feld]: neu.id }));
              if (p.onAdressenGeaendert === undefined) void nachladen(); else p.onAdressenGeaendert();
            }} />
        );
      })()}
    </div>
  );
}
